<template>
  <v-container class="py-6" max-width="900">
    <div class="d-flex align-center mb-6">
      <v-icon color="primary" size="32" class="mr-3">mdi-brain</v-icon>
      <div>
        <h1 class="text-h5 font-weight-bold">Stillings-analyse</h1>
        <p class="text-body-2 text-medium-emphasis mb-0">
          Analysér backgammon-stillinger med GNU Backgammon's neurale netværk
        </p>
      </div>
    </div>

    <!-- Input -->
    <v-card variant="outlined" class="mb-4">
      <v-card-text>
        <v-text-field
          v-model="xgid"
          label="XGID position string"
          placeholder="XGID=-b----E-C---eE---c-e----B-:0:0:1:31:0:0:0:0:10"
          variant="outlined"
          density="compact"
          hint="Brug XGID-format — dice 00 = terningebeslutning, D = tag/giv op"
          persistent-hint
          class="mb-3"
          @keydown.enter="analyze"
        />

        <div class="d-flex align-center ga-3 flex-wrap">
          <v-select
            v-model="depth"
            :items="depthOptions"
            label="Analysedybde"
            variant="outlined"
            density="compact"
            hide-details
            style="max-width: 200px"
          />
          <v-btn
            color="primary"
            variant="flat"
            :loading="loading"
            :disabled="!xgid.trim()"
            @click="analyze"
          >
            <v-icon class="mr-1">mdi-magnify</v-icon>
            Analysér
          </v-btn>

          <v-spacer />

          <v-btn-group variant="outlined" density="compact">
            <v-btn
              v-for="example in examples"
              :key="example.label"
              size="small"
              @click="loadExample(example)"
            >
              {{ example.label }}
            </v-btn>
          </v-btn-group>
        </div>
      </v-card-text>
    </v-card>

    <!-- Error -->
    <v-alert v-if="error" type="error" variant="tonal" class="mb-4" closable @click:close="error = ''">
      {{ error }}
    </v-alert>

    <!-- Results: Cube Decision -->
    <v-card v-if="result && result.action !== 'play'" variant="outlined" class="mb-4">
      <v-card-title class="d-flex align-center">
        <v-icon class="mr-2" size="20">mdi-cube-outline</v-icon>
        Terningebeslutning
      </v-card-title>
      <v-divider />
      <v-card-text>
        <div class="text-center py-4">
          <v-chip :color="cubeActionColor" size="x-large" variant="flat" class="text-h6 pa-6">
            {{ cubeActionLabel }}
          </v-chip>
        </div>

        <v-table density="compact" class="mt-4">
          <thead>
            <tr>
              <th>Scenarie</th>
              <th class="text-right">Equity</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Ingen dobling</td>
              <td class="text-right font-weight-medium">{{ result.data.equity[0]?.toFixed(4) }}</td>
            </tr>
            <tr>
              <td>Dobling, tag</td>
              <td class="text-right font-weight-medium">{{ result.data.equity[1]?.toFixed(4) }}</td>
            </tr>
            <tr>
              <td>Dobling, giv op</td>
              <td class="text-right font-weight-medium">{{ result.data.equity[2]?.toFixed(4) }}</td>
            </tr>
            <tr class="font-weight-bold">
              <td>Optimal</td>
              <td class="text-right">{{ result.data.equity[3]?.toFixed(4) }}</td>
            </tr>
          </tbody>
        </v-table>

        <!-- Win chances if available -->
        <div v-if="result.data.wc" class="mt-4">
          <div class="text-caption text-medium-emphasis mb-2">Vindchancer</div>
          <div class="d-flex ga-3 flex-wrap">
            <v-chip size="small" variant="tonal" color="success">
              Vind: {{ (result.data.wc[0] * 100).toFixed(1) }}%
            </v-chip>
            <v-chip size="small" variant="tonal" color="info">
              Gammon: {{ (result.data.wc[1] * 100).toFixed(1) }}%
            </v-chip>
            <v-chip size="small" variant="tonal" color="primary">
              BG: {{ (result.data.wc[2] * 100).toFixed(1) }}%
            </v-chip>
            <v-chip size="small" variant="tonal" color="warning">
              Tab gammon: {{ (result.data.wc[3] * 100).toFixed(1) }}%
            </v-chip>
            <v-chip size="small" variant="tonal" color="error">
              Tab BG: {{ (result.data.wc[4] * 100).toFixed(1) }}%
            </v-chip>
          </div>
        </div>
      </v-card-text>
    </v-card>

    <!-- Results: Move Analysis -->
    <v-card v-if="result && result.action === 'play'" variant="outlined">
      <v-card-title class="d-flex align-center">
        <v-icon class="mr-2" size="20">mdi-dice-multiple</v-icon>
        Træk-analyse
        <v-chip class="ml-2" size="small" variant="tonal" color="primary">
          {{ result.data.length }} træk
        </v-chip>
      </v-card-title>
      <v-divider />

      <v-table density="comfortable" hover>
        <thead>
          <tr>
            <th style="width: 40px">#</th>
            <th>Træk</th>
            <th class="text-right">Equity</th>
            <th class="text-right">Diff</th>
            <th class="text-right">Vind %</th>
            <th class="text-right">Gammon %</th>
            <th class="text-right">BG %</th>
            <th class="text-right">Tab G %</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(move, i) in result.data"
            :key="i"
            :class="{ 'bg-green-lighten-5': i === 0 }"
          >
            <td>
              <v-chip v-if="i === 0" size="x-small" color="success" variant="flat">★</v-chip>
              <span v-else class="text-medium-emphasis">{{ i + 1 }}</span>
            </td>
            <td class="font-weight-medium font-mono">{{ move.move }}</td>
            <td class="text-right" :class="equityClass(move.equity[0])">
              {{ move.equity[0] >= 0 ? '+' : '' }}{{ move.equity[0].toFixed(3) }}
            </td>
            <td class="text-right text-medium-emphasis">
              {{ i === 0 ? '—' : (move.equity[0] - result.data[0].equity[0]).toFixed(3) }}
            </td>
            <td class="text-right">{{ (move.eval[0] * 100).toFixed(1) }}%</td>
            <td class="text-right">{{ (move.eval[1] * 100).toFixed(1) }}%</td>
            <td class="text-right">{{ (move.eval[2] * 100).toFixed(1) }}%</td>
            <td class="text-right">{{ (move.eval[3] * 100).toFixed(1) }}%</td>
          </tr>
        </tbody>
      </v-table>
    </v-card>

    <!-- Performance info -->
    <div v-if="analysisTime" class="text-caption text-medium-emphasis text-right mt-2">
      Analyse udført på {{ analysisTime }}ms ved dybde {{ depth }}
    </div>
  </v-container>
</template>

<script setup>
import { ref, computed } from "vue";
import { analyzePosition } from "../services/analyzeApi";

const xgid = ref("");
const depth = ref(2);
const loading = ref(false);
const result = ref(null);
const error = ref("");
const analysisTime = ref(null);

const depthOptions = [
  { title: "0-ply (hurtig)", value: 0 },
  { title: "1-ply", value: 1 },
  { title: "2-ply (anbefalet)", value: 2 },
  { title: "3-ply (langsom)", value: 3 },
];

const examples = [
  {
    label: "Åbning 3-1",
    xgid: "XGID=-b----E-C---eE---c-e----B-:0:0:1:31:0:0:0:0:10",
  },
  {
    label: "Dobling?",
    xgid: "XGID=aa--BBBB----dE---d-e----B-:0:0:1:00:0:0:0:0:10",
  },
  {
    label: "Tag/giv op?",
    xgid: "XGID=aa--BBBB----dE---d-e----B-:0:0:1:D:0:0:0:0:10",
  },
  {
    label: "Middel",
    xgid: "XGID=-a--aBCbCCC-aaaa---c-bbA--:0:0:-1:64:7:7:0:17:10",
  },
];

const cubeActionLabel = computed(() => {
  if (!result.value) return "";
  const map = {
    double: "Dobl!",
    roll: "Slå (ikke dobl)",
    take: "Tag!",
    drop: "Giv op!",
  };
  return map[result.value.action] || result.value.action;
});

const cubeActionColor = computed(() => {
  if (!result.value) return "grey";
  const map = {
    double: "warning",
    roll: "success",
    take: "info",
    drop: "error",
  };
  return map[result.value.action] || "grey";
});

function equityClass(equity) {
  if (equity > 0.1) return "text-success";
  if (equity < -0.1) return "text-error";
  return "";
}

function loadExample(example) {
  xgid.value = example.xgid;
  analyze();
}

async function analyze() {
  if (!xgid.value.trim()) return;

  loading.value = true;
  error.value = "";
  result.value = null;
  analysisTime.value = null;

  const start = performance.now();

  try {
    const { data } = await analyzePosition(xgid.value.trim(), depth.value);
    result.value = data;
    analysisTime.value = Math.round(performance.now() - start);
  } catch (err) {
    error.value = err.response?.data?.error || err.message || "Analyse fejlede";
  }

  loading.value = false;
}
</script>

<style scoped>
.font-mono {
  font-family: "Roboto Mono", monospace;
}
</style>